import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { addEvent, updateEvent } from '../../../reducers/seriesReducer'
import { getMomentInCommunityTimezone, getMomentInCommunityTimezoneReversed } from '../../../utility/datesHelper'
import TwoButtonToggle from '../../common/form/TwoButtonToggle'
import SubmitButton from '../../common/buttons/SubmitButton'
import DatePicker from '../../common/form/DatePicker'
import Input from '../../common/form/Input'
import Modal from '../../common/Modal'
import Toggle from '../../common/Toggle'

import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon.svg'
import { ReactComponent as TimeIcon } from '../../../assets/images/common/icons/time-icon.svg'
import { ReactComponent as LocationIcon } from '../../../assets/images/common/icons/location-icon.svg'

import styles from './EventModal.module.scss'
import FieldError from '../../common/FieldError'
import MCElement from '../../common/MCElement'
import classNames from 'classnames'
import moment from 'moment'

import colors from '../../../assets/styles/globals.scss'

const EMPTY_EVENT = { eventDate: '', format: '', name: '', location: '', description: '', hasWaitlistEnabled: false }

const EventModal = ({ showModal, closeModal, selectedEvent }) => {//eslint-disable-line
  const { t } = useTranslation()
  const customErrorMessages = {
    eventDate: t('events.seriesFlow.eventModal.errorSelectDate'),
    eventTime: t('events.seriesFlow.eventModal.errorSelectTime'),
    eventDateInThePast: t('events.seriesFlow.eventModal.errorDateInPast'),
    format: t('events.seriesFlow.eventModal.errorSelectFormat'),
    spotsAvailable: t('events.seriesFlow.eventModal.errorSpotsAvailable'),
    hasWaitlistEnabled: t('events.seriesFlow.eventModal.errorWaitlistDisable'),
  }
  const dispatch = useDispatch()
  const timezone = useSelector(state => state.user.current_user.communities[0].timezone)
  const capacityEditDisabled = !!selectedEvent?.waitlistedEnrollmentsCount
  const [event, setEvent] = useState(EMPTY_EVENT)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (selectedEvent) {
      const dateInTimezone = getMomentInCommunityTimezoneReversed(moment(selectedEvent.eventDate), timezone, 'YYYY-MM-DD HH:mm:ss')?.toDate()
      setEvent({ ...selectedEvent, eventDate: dateInTimezone })
    }
  }, [showModal, selectedEvent, timezone])

  const handleInputChange = e => {
    const { name, value } = e.target
    if (name === 'spotsAvailable' & value.length > 5) {
      return
    }

    setEvent({ ...event, [name]: value })
  }

  const handleSelectFormat = (format) => {
    if (event.format !== format) {
      setEvent({ ...event, format: format })
    }
  }

  const handleDateTimeChange = e => {
    const { name, value } = e

    if (name === 'date') {
      let eventDate = event.eventDate ? moment(event.eventDate) : moment(new Date().setMinutes(0))
      eventDate = moment(moment(value).format('YYYY-MM-DD') + ' ' + eventDate.format('HH:mm')).toDate()
      setEvent({ ...event, eventDate: eventDate })
    } else if (name === 'eventDate') {
      let eventDate = event.eventDate ? moment(event.eventDate) : moment(new Date())
      eventDate = moment(eventDate.format('YYYY-MM-DD') + ' ' + moment(value).format('HH:mm')).toDate()
      setEvent({ ...event, eventDate: eventDate })
    }
  }

  const handleCapacityToggle = () => {
    setEvent({ ...event, enableEventCapacity: !event.enableEventCapacity, hasWaitlistEnabled: false })
  }

  const handleWaitlistToggle = (value) => {
    setEvent({ ...event, hasWaitlistEnabled: value })
    setErrors({ ...errors, hasWaitlistEnabled: '' })
  }

  const invalidFields = {
    eventDate: event.eventDate === '',
    eventTime: event.eventDate === '',
    format: event.format === '',
    spotsAvailable: event.enableEventCapacity && event.spotsAvailable < event.confirmedEnrollmentsCount,
    hasWaitlistEnabled: !event.hasWaitlistEnabled && !!selectedEvent?.waitlistedEnrollmentsCount,
  }

  const formValid = () => {
    const errors = {};

    ['eventDate', 'eventTime', 'format', 'spotsAvailable', 'hasWaitlistEnabled'].forEach((type) => {
      if (invalidFields[type]) {
        errors[type] = customErrorMessages[type]
      }
    })
    setErrors(errors)

    return Object.keys(errors).length === 0
  }

  const submitEvent = () => {
    if (formValid()) {
      const newEvent = {
        ...event,
        eventDate: getMomentInCommunityTimezone(moment(event.eventDate), timezone, 'YYYY-MM-DD HH:mm:ss').toDate(),
      }
      if (event.index === undefined) {
        dispatch(addEvent(newEvent))
      } else {
        dispatch(updateEvent(newEvent))
      }
      exitModal()
    }
  }

  const exitModal = () => {
    setEvent(EMPTY_EVENT)
    setErrors({})
    closeModal()
  }

  return (
    <Modal
      isOpen={showModal}
      onClose={exitModal}
      className='single-session-builder-modal-content'
    >
      <div className={styles['event-modal']}>
        <div className='animated fadeIn'>
          <div>
            <p className={styles['title']}>{t('events.seriesFlow.eventModal.title')}</p>
            <div className='row'>

              <div className='col-12 col-sm-6 mb-2'>
                <label htmlFor='date' className={styles['label-text']}>
                  {t('common.field.date')} <span className='required-text'>*</span>
                </label>

                <DatePicker
                  selected={event.eventDate}
                  onChange={date => handleDateTimeChange({ name: 'date', value: date })}
                  error={errors.eventDate || errors.eventDateInThePast}
                  symbol={<CalendarIcon className={styles.inputSymbol} />}
                />
              </div>

              <div className='col-12 col-sm-6 mb-2'>
                <label htmlFor='time' className={styles['label-text']}>
                  {t('events.seriesFlow.eventModal.startTimeLabel')} <span className='required-text'>*</span>
                </label>

                <DatePicker
                  selected={event.eventDate}
                  className='eventDateInput'
                  onChange={time => handleDateTimeChange({ name: 'eventDate', value: time })}
                  inputClassNames={{
                    inputContainer: classNames('', { 'error-input': !!errors.eventDate }),
                  }}
                  symbol={<TimeIcon className={styles.inputSymbol} />}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption='time'
                  dateFormat='h:mm aa'
                  autoComplete='off'
                  error={errors.eventTime}
                />
              </div>

              <div className='col-12'>
                <label htmlFor='format' className={styles['label-text']}>
                  {t('events.seriesFlow.eventModal.formatLabel')} <span className='required-text'>*</span>
                </label>

                <div
                  onClick={() => handleSelectFormat('Tee Times')}
                  className='d-flex format-option-tee-times mb-2'
                  role='button'
                  onKeyDown={null}
                  tabIndex={0}
                >
                  <MCElement
                    type='radio'
                    onKeyDown={(e) => {
                      e.key === 'Enter' && handleSelectFormat('Tee Times')
                    }}
                    primaryColor={event.format === 'Tee Times' ? colors.secondaryColor : colors.lightNavy}
                    isSelected={event.format === 'Tee Times'}
                  />
                  <span className='ml-2'>{t('events.seriesFlow.eventModal.formatTeeTimes')}</span>
                </div>
                <div
                  onClick={() => handleSelectFormat('Shotgun')}
                  className='d-flex format-option-shotgun'
                  role='button'
                  onKeyDown={null}
                  tabIndex={0}
                >
                  <MCElement
                    type='radio'
                    onKeyDown={(e) => {
                      e.key === 'Enter' && handleSelectFormat('Shotgun')
                    }}
                    primaryColor={event.format === 'Shotgun' ? colors.secondaryColor : colors.lightNavy}
                    isSelected={event.format === 'Shotgun'}
                  />
                  <span className='ml-2'>{t('events.seriesFlow.eventModal.formatShotgun')}</span>
                </div>
                {!!errors.format && <FieldError message={errors.format} />}
              </div>

              <div className='col-12'>
                <div className='d-flex align-items-center'>
                  <div className={classNames('d-flex align-items-center py-3', styles.toggleWrapper)}>
                    <Toggle
                      icons={false}
                      name='enableEventCapacity'
                      checked={event.enableEventCapacity}
                      onChange={handleCapacityToggle}
                    />
                    <label htmlFor='enableEventCapacity' className='ml-3 mb-0'>{t('events.seriesFlow.eventModal.enableEventCapacity')}</label>
                  </div>
                </div>
                {event.enableEventCapacity && (
                  <div className='mb-3'>
                    <div className='d-flex align-items-center'>
                      {t('events.seriesFlow.eventModal.spotsAvailable')} <span className='required-text'>*</span>
                      <div className={styles.spotsAvailable}>
                        <Input
                          disabled={capacityEditDisabled}
                          name='spotsAvailable'
                          type='number'
                          onChange={handleInputChange}
                          value={event.spotsAvailable}
                          styles={errors.spotsAvailable && { inputContainer: { borderColor: '#cc0000' } }}
                        />
                      </div>
                    </div>
                    {!!errors.spotsAvailable && <FieldError message={`${errors.spotsAvailable} ${event.confirmedEnrollmentsCount}`} />}
                    {capacityEditDisabled && <FieldError message={t('events.seriesFlow.eventModal.errorCapacityLocked')}/>}
                    <div className='d-flex mt-3'>
                      <div className={classNames('pr-5', { 'disabled': event.spotsAvailable < 1 })}>
                        {t('events.seriesFlow.eventModal.waitlist')}
                      </div>
                      <div className='d-flex align-items-center col-md-6 col-sm-4'>
                        <TwoButtonToggle
                          checked={event.hasWaitlistEnabled}
                          onChange={(value) => handleWaitlistToggle(value)}
                          disabled={event.spotsAvailable < 1}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!!errors.hasWaitlistEnabled && <FieldError message={errors.hasWaitlistEnabled} />}
              </div>

              <div className={styles['hl']}/>

              <div className='col-12 mb-2'>
                <label htmlFor='title' className={styles['label-text']}>{t('common.field.title')}</label>
                <Input
                  name='name'
                  type='text'
                  onChange={handleInputChange}
                  placeholder={t('events.seriesFlow.eventModal.titlePlaceholder')}
                  value={event.name}
                  error={errors.name}
                />
              </div>

              <div className='col-12 mb-3'>
                <label htmlFor='location' className={styles['label-text']}>{t('common.field.location')}</label>
                <Input
                  name='location'
                  type='text'
                  onChange={handleInputChange}
                  placeholder={t('events.seriesFlow.eventModal.locationPlaceholder')}
                  value={event.location}
                  error={errors.location}
                  symbol={<LocationIcon className={styles.inputSymbol}/>}
                />
              </div>

              <div className='col-12 mb-3'>
                <label htmlFor='description' className={styles['label-text']}>{t('common.field.description')}</label>
                <Input
                  name='description'
                  type='textarea'
                  onChange={handleInputChange}
                  placeholder={t('events.seriesFlow.eventModal.descriptionPlaceholder')}
                  value={event.description}
                  error={errors.description}
                />
              </div>
            </div>
            <div className='row py-3'>
              <div className='col-6'>
                <SubmitButton
                  onClick={exitModal}
                  buttonMessage={t('common.cancel')}
                  buttonColor='navy-button'
                />
              </div>
              <div className='col-6'>
                <SubmitButton
                  onClick={submitEvent}
                  buttonMessage={event.index === undefined
                    ? t('common.action.create')
                    : t('common.action.update')}
                  buttonColor='orange-button'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

EventModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  selectedEvent: PropTypes.object,
}

export default EventModal
